import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';
import { errorAlert } from '@xdocs/schemas';
import { safeT } from '../i18n/safeT.js';
import type { Locale } from '@xdocs/i18n';

/**
 * WS-op analog of `auth/applyServerAlert.ts` — that helper is typed
 * specifically to `AuthTransportError` (the pre-handshake-only transport,
 * see its own docstring); every op sent through `requestTyped`/`WsClient.
 * request()` instead REJECTS the promise directly with an `ErrorAlert`-
 * shaped object (`{code, message, fields}` — see `useJobSnapshot.ts`'s
 * `useQuery<T, ErrorAlert>` typing and `client.ts`'s `correlation.reject`
 * call sites, which pass the parsed `err` envelope payload straight
 * through, never wrapped in a custom `Error`).
 *
 * Round-trip parity (CODE_GUIDELINES §5.4/§16.1): a `VALIDATION` alert's
 * `fields[]` are the SAME shared zod schema's catalog-key messages the form
 * already validates client-side against `zodResolver`, so they map 1:1 onto
 * `setError`. Any other alert (`NOT_FOUND`/`CONFLICT`/`FORBIDDEN`/…) has no
 * field to attach to — the caller renders the returned banner message
 * inline atop the form (ux.md: page-level error atop the affected region).
 *
 * Returns the localized banner message, or `null` when the alert was fully
 * absorbed into field errors.
 */
export function applyWsAlert<TFieldValues extends FieldValues>(
  err: unknown,
  setError: UseFormSetError<TFieldValues>,
  locale: Locale,
): string | null {
  const parsed = errorAlert.safeParse(err);
  if (!parsed.success) return safeT(locale, 'errors:code.INTERNAL');

  const alert = parsed.data;
  if (alert.code === 'VALIDATION' && alert.fields && alert.fields.length > 0) {
    for (const field of alert.fields) {
      setError(field.path as Path<TFieldValues>, { type: 'server', message: safeT(locale, field.message) });
    }
    return null;
  }
  return safeT(locale, alert.message);
}
